import { useState, useEffect, useRef, FormEvent, ChangeEvent } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';

const INDEX_PRESETS = ['KOSPI', 'KOSDAQ', 'SP500', 'NASDAQ'];
const MARKET_STATES = ['UP', 'FLAT', 'DOWN'];

interface DailyPlanItem {
    index: string;
    state: string;
    action: string;
    memo: string;
}

interface DailyPlanVersion {
    id: string;
    planDate: string;
    version: number;
    pinned: boolean;
    items: DailyPlanItem[];
    globalMemo: string | null;
    createdAt: string;
}

interface Position {
    id: string;
    symbol: string;
    market: string;
    status: string;
    pnlPct: number | null;
}

interface JournalImage {
    id: string;
    r2Key: string;
    url: string;
    caption: string | null;
}

interface UploadingFile {
    file: File;
    preview: string;
    r2Key?: string;
    url?: string;
    uploading: boolean;
    error?: string;
}

export default function Journal() {
    const { date } = useParams<{ date: string }>();
    const [planVersions, setPlanVersions] = useState<DailyPlanVersion[]>([]);
    const [activePlan, setActivePlan] = useState<DailyPlanVersion | null>(null);
    const [positions, setPositions] = useState<Position[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    // Form state for new plan
    const [showForm, setShowForm] = useState(false);
    const [items, setItems] = useState<DailyPlanItem[]>([
        { index: 'KOSPI', state: 'FLAT', action: '', memo: '' },
    ]);
    const [globalMemo, setGlobalMemo] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Image upload state
    const [savedImages, setSavedImages] = useState<JournalImage[]>([]);
    const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Use Korean timezone (UTC+9) for today's date if not provided in URL
    const kstNow = new Date(Date.now() + (9 * 60 * 60 * 1000));
    const todayKST = kstNow.toISOString().split('T')[0];
    const today = new Date(date || todayKST);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];

    useEffect(() => {
        loadData();
    }, [date]);

    async function loadData() {
        setIsLoading(true);

        // Load today's active plan
        const activeResponse = await api.getActivePlan(date);
        if (activeResponse.success) {
            setActivePlan(activeResponse.data);
        }

        // Load tomorrow's plan versions (for editing)
        const versionsResponse = await api.getPlanVersions(tomorrowStr);
        if (versionsResponse.success) {
            setPlanVersions(versionsResponse.data || []);
        }

        // Load positions (mock - would filter by date in real app)
        const posResponse = await api.getPositions();
        if (posResponse.success) {
            setPositions((posResponse.data || []).slice(0, 5));
        }

        // Load journal images for this date
        const journalDate = date || todayKST;
        const imagesResponse = await api.getJournalImages(journalDate);
        if (imagesResponse.success) {
            setSavedImages(imagesResponse.data || []);
        }

        setIsLoading(false);
    }

    function addItem() {
        setItems(prev => [...prev, { index: 'KOSPI', state: 'FLAT', action: '', memo: '' }]);
    }

    function removeItem(index: number) {
        setItems(prev => prev.filter((_, i) => i !== index));
    }

    function updateItem(index: number, field: keyof DailyPlanItem, value: string) {
        setItems(prev => {
            const newItems = [...prev];
            newItems[index] = { ...newItems[index], [field]: value };
            return newItems;
        });
    }

    async function handleSubmitPlan(e: FormEvent) {
        e.preventDefault();

        if (items.some(item => !item.action.trim())) {
            alert('모든 시나리오에 액션을 입력해주세요');
            return;
        }

        setIsSubmitting(true);

        const response = await api.createPlanVersion({
            planDate: tomorrowStr,
            items,
            globalMemo: globalMemo || undefined,
        });

        if (response.success) {
            setShowForm(false);
            setItems([{ index: 'KOSPI', state: 'FLAT', action: '', memo: '' }]);
            setGlobalMemo('');
            loadData();
        }

        setIsSubmitting(false);
    }

    async function handlePinVersion(id: string) {
        await api.pinPlanVersion(id);
        loadData();
    }

    function loadVersionToEdit(version: DailyPlanVersion) {
        setItems(version.items);
        setGlobalMemo(version.globalMemo || '');
        setShowForm(true);
    }

    // Image compression utility
    function compressImage(file: File, maxWidth: number = 1920, quality: number = 0.8): Promise<File> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target?.result as string;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    if (width > maxWidth) {
                        height = (height * maxWidth) / width;
                        width = maxWidth;
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx?.drawImage(img, 0, 0, width, height);

                    canvas.toBlob((blob) => {
                        if (!blob) {
                            reject(new Error('Canvas to Blob failed'));
                            return;
                        }
                        const compressedFile = new File([blob], file.name, {
                            type: file.type,
                            lastModified: Date.now(),
                        });
                        resolve(compressedFile);
                    }, file.type, quality);
                };
                img.onerror = (error) => reject(error);
            };
            reader.onerror = (error) => reject(error);
        });
    }

    async function handleFileSelect(e: ChangeEvent<HTMLInputElement>) {
        const selectedFiles = e.target.files;
        if (!selectedFiles) return;

        const filePromises = Array.from(selectedFiles).map(async (file) => {
            try {
                const compressedFile = await compressImage(file);
                return {
                    file: compressedFile,
                    preview: URL.createObjectURL(compressedFile),
                    uploading: false,
                } as UploadingFile;
            } catch {
                return {
                    file,
                    preview: URL.createObjectURL(file),
                    uploading: false,
                } as UploadingFile;
            }
        });

        const newFiles = await Promise.all(filePromises);
        setUploadingFiles(prev => [...prev, ...newFiles]);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }

    function removeUploadingFile(index: number) {
        setUploadingFiles(prev => {
            const newFiles = [...prev];
            URL.revokeObjectURL(newFiles[index].preview);
            newFiles.splice(index, 1);
            return newFiles;
        });
    }

    async function uploadSingleFile(index: number) {
        const file = uploadingFiles[index];
        if (file.uploading || file.r2Key) return;

        setUploadingFiles(prev => {
            const newFiles = [...prev];
            newFiles[index] = { ...newFiles[index], uploading: true, error: undefined };
            return newFiles;
        });

        try {
            const uploadResponse = await api.uploadFile(file.file);

            if (!uploadResponse.success) {
                throw new Error(uploadResponse.error || '업로드 실패');
            }

            const { fileKey, fileUrl } = uploadResponse.data;

            // Register image in DB
            const journalDate = date || todayKST;
            const registerResponse = await api.addJournalImage({
                journalDate,
                r2Key: fileKey,
                url: fileUrl,
            });

            if (registerResponse.success) {
                // Remove from uploading files and reload saved images
                setUploadingFiles(prev => prev.filter((_, i) => i !== index));
                loadData();
            } else {
                throw new Error(registerResponse.error || 'DB 등록 실패');
            }
        } catch (e: any) {
            setUploadingFiles(prev => {
                const newFiles = [...prev];
                newFiles[index] = { ...newFiles[index], uploading: false, error: e.message || '업로드 실패' };
                return newFiles;
            });
        }
    }

    async function uploadAllFiles() {
        for (let i = 0; i < uploadingFiles.length; i++) {
            if (!uploadingFiles[i].r2Key && !uploadingFiles[i].error) {
                await uploadSingleFile(i);
            }
        }
    }

    async function deleteSavedImage(id: string) {
        if (!confirm('이 이미지를 삭제하시겠습니까?')) return;

        const response = await api.deleteJournalImage(id);
        if (response.success) {
            loadData();
        }
    }

    const formatPnl = (pnl: number | null | undefined) => {
        if (pnl == null) return '-';
        const formatted = pnl.toFixed(2);
        return pnl >= 0 ? `+${formatted}%` : `${formatted}%`;
    };

    const getPnlClass = (pnl: number | null | undefined) => {
        if (pnl == null) return '';
        return pnl >= 0 ? 'positive' : 'negative';
    };

    const getStateEmoji = (state: string) => {
        switch (state) {
            case 'UP': return '📈';
            case 'DOWN': return '📉';
            default: return '➡️';
        }
    };

    if (isLoading) {
        return (
            <div className="loading-screen">
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="page-header">
                <h1 className="page-title">일지 - {date}</h1>
                <div className="flex gap-sm">
                    <Link
                        to={`/journal/${new Date(new Date(date!).getTime() - 86400000).toISOString().split('T')[0]}`}
                        className="btn btn-ghost"
                    >
                        ← 어제
                    </Link>
                    <Link
                        to={`/journal/${new Date(new Date(date!).getTime() + 86400000).toISOString().split('T')[0]}`}
                        className="btn btn-ghost"
                    >
                        내일 →
                    </Link>
                </div>
            </div>

            {/* Today's Scenario (from yesterday's plan) */}
            <div className="card mb-lg">
                <div className="card-header">
                    <h3 className="card-title">오늘의 시나리오</h3>
                    {activePlan && (
                        <span className="badge badge-info">v{activePlan.version}</span>
                    )}
                </div>

                {activePlan ? (
                    <>
                        {activePlan.items.map((item, idx) => (
                            <div key={idx} className="scenario-card">
                                <div className="scenario-header">
                                    <span className="scenario-index">{item.index}</span>
                                    <span className={`scenario-state ${item.state.toLowerCase()}`}>
                                        {getStateEmoji(item.state)} {item.state === 'UP' ? '상승' : item.state === 'DOWN' ? '하락' : '보합'}
                                    </span>
                                </div>
                                <p className="font-semibold mb-sm">{item.action}</p>
                                {item.memo && <p className="text-sm text-muted">{item.memo}</p>}
                            </div>
                        ))}
                        {activePlan.globalMemo && (
                            <p className="text-sm text-muted mt-md">
                                <strong>메모:</strong> {activePlan.globalMemo}
                            </p>
                        )}
                    </>
                ) : (
                    <div className="empty-state">
                        <p>오늘의 시나리오가 없습니다</p>
                    </div>
                )}
            </div>
            {/* Today's Portfolio */}
            <div className="card mb-lg">
                <div className="card-header">
                    <h3 className="card-title">
                        <Link to="/new" className="text-decoration-none hover-underline">오늘의 포트폴리오</Link>
                    </h3>
                    <div className="flex gap-sm">
                        <Link to="/new" className="btn btn-primary btn-sm">등록하기</Link>
                        <Link to="/positions" className="btn btn-secondary btn-sm">모두 보기</Link>
                    </div>
                </div>

                {positions.length > 0 ? (
                    <div className="flex flex-col gap-sm">
                        {positions.map((pos) => (
                            <Link key={pos.id} to={`/positions/${pos.id}`} className="position-card">
                                <div className="position-info">
                                    <div>
                                        <div className="position-symbol">{pos.symbol}</div>
                                        <div className="position-market">{pos.market}</div>
                                    </div>
                                    <span className={`badge ${pos.status === 'OPEN' ? 'badge-success' : 'badge-neutral'}`}>
                                        {pos.status === 'OPEN' ? '보유중' : '청산'}
                                    </span>
                                </div>
                                <div className={`position-pnl ${getPnlClass(pos.pnlPct)}`}>
                                    {formatPnl(pos.pnlPct)}
                                </div>
                            </Link>
                        ))}
                    </div>
                ) : (
                    <div className="empty-state">
                        <p>포트폴리오가 없습니다</p>
                    </div>
                )}
            </div>

            {/* Journal Images */}
            <div className="card mb-lg">
                <div className="card-header">
                    <h3 className="card-title">계좌 현황 이미지</h3>
                </div>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                />

                <div
                    className="upload-zone mb-md"
                    onClick={() => fileInputRef.current?.click()}
                >
                    <p>클릭하여 이미지 선택</p>
                    <p className="text-sm text-muted">계좌 잔고, 수익률 스크린샷 등</p>
                </div>

                {/* Uploading files */}
                {uploadingFiles.length > 0 && (
                    <div className="mb-md">
                        <p className="text-sm font-semibold mb-sm">업로드 대기 ({uploadingFiles.length})</p>
                        <div className="image-gallery">
                            {uploadingFiles.map((file, idx) => (
                                <div key={idx} className="gallery-item" style={{ position: 'relative' }}>
                                    <img src={file.preview} alt={file.file.name} />
                                    <div className="gallery-item-overlay">
                                        {file.uploading ? (
                                            <div className="loading-spinner" style={{ width: 24, height: 24 }}></div>
                                        ) : file.error ? (
                                            <span className="badge badge-danger">{file.error}</span>
                                        ) : (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary"
                                                onClick={(e) => { e.stopPropagation(); uploadSingleFile(idx); }}
                                            >
                                                업로드
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={(e) => { e.stopPropagation(); removeUploadingFile(idx); }}
                                            style={{ marginLeft: '4px' }}
                                        >
                                            ×
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            className="btn btn-secondary mt-sm"
                            onClick={uploadAllFiles}
                        >
                            전체 업로드
                        </button>
                    </div>
                )}

                {/* Saved images */}
                {savedImages.length > 0 ? (
                    <div className="image-gallery">
                        {savedImages.map((img) => (
                            <div key={img.id} className="gallery-item" style={{ position: 'relative' }}>
                                <img src={img.url} alt={img.caption || '계좌 현황'} />
                                <div className="gallery-item-overlay">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-danger"
                                        onClick={(e) => { e.stopPropagation(); deleteSavedImage(img.id); }}
                                    >
                                        삭제
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-muted">등록된 이미지가 없습니다</p>
                )}
            </div>

            {/* Tomorrow's Plan */}
            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">내일({tomorrowStr}) 시나리오</h3>
                    <button
                        onClick={() => setShowForm(!showForm)}
                        className="btn btn-primary btn-sm"
                    >
                        {showForm ? '취소' : '+ 새 버전'}
                    </button>
                </div>

                {/* Plan Form */}
                {showForm && (
                    <form onSubmit={handleSubmitPlan} className="mb-lg" style={{ paddingTop: 'var(--space-md)' }}>
                        {items.map((item, idx) => (
                            <div key={idx} className="scenario-card">
                                <div className="flex gap-md mb-md">
                                    <select
                                        className="form-input form-select"
                                        value={item.index}
                                        onChange={(e) => updateItem(idx, 'index', e.target.value)}
                                        style={{ width: 'auto' }}
                                    >
                                        {INDEX_PRESETS.map(p => (
                                            <option key={p} value={p}>{p}</option>
                                        ))}
                                    </select>

                                    <select
                                        className="form-input form-select"
                                        value={item.state}
                                        onChange={(e) => updateItem(idx, 'state', e.target.value)}
                                        style={{ width: 'auto' }}
                                    >
                                        {MARKET_STATES.map(s => (
                                            <option key={s} value={s}>
                                                {getStateEmoji(s)} {s === 'UP' ? '상승' : s === 'DOWN' ? '하락' : '보합'}
                                            </option>
                                        ))}
                                    </select>

                                    {items.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeItem(idx)}
                                            className="btn btn-ghost btn-sm text-danger"
                                        >
                                            삭제
                                        </button>
                                    )}
                                </div>

                                <input
                                    type="text"
                                    className="form-input mb-sm"
                                    value={item.action}
                                    onChange={(e) => updateItem(idx, 'action', e.target.value)}
                                    placeholder="이 상황에서의 액션 (필수)"
                                    required
                                />

                                <input
                                    type="text"
                                    className="form-input"
                                    value={item.memo}
                                    onChange={(e) => updateItem(idx, 'memo', e.target.value)}
                                    placeholder="추가 메모..."
                                />
                            </div>
                        ))}

                        <button type="button" onClick={addItem} className="btn btn-secondary btn-sm mb-md">
                            + 시나리오 추가
                        </button>

                        <div className="form-group">
                            <label className="form-label">전체 메모</label>
                            <textarea
                                className="form-input form-textarea"
                                value={globalMemo}
                                onChange={(e) => setGlobalMemo(e.target.value)}
                                placeholder="오늘의 전체적인 시황 및 주의사항..."
                            />
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? '저장 중...' : '시나리오 저장'}
                        </button>
                    </form>
                )}

                {/* Existing Versions */}
                {planVersions.length > 0 && (
                    <div className="mt-lg">
                        <h4 className="text-sm font-semibold text-muted mb-md">저장된 버전</h4>
                        {planVersions.map((ver) => (
                            <div key={ver.id} className="card mb-md" style={{ border: ver.pinned ? '1px solid var(--color-primary)' : '1px solid var(--color-border)' }}>
                                <div className="card-header mb-sm" style={{ borderBottom: '1px solid var(--color-border-light)', paddingBottom: '8px' }}>
                                    <div className="flex items-center gap-sm">
                                        <span className={`badge ${ver.pinned ? 'badge-primary' : 'badge-neutral'}`}>v{ver.version}</span>
                                        {ver.pinned && <span className="text-xs text-primary font-bold">활성 시나리오</span>}
                                        <span className="text-xs text-muted">{new Date(ver.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                    </div>
                                    <div className="flex gap-sm">
                                        <button
                                            onClick={() => loadVersionToEdit(ver)}
                                            className="btn btn-ghost btn-sm"
                                        >
                                            ✏️ 수정
                                        </button>
                                        {!ver.pinned && (
                                            <button
                                                onClick={() => handlePinVersion(ver.id)}
                                                className="btn btn-ghost btn-sm"
                                            >
                                                이 버전 활성화
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {ver.items.map((item, idx) => (
                                    <div key={idx} className="scenario-card mb-sm">
                                        <div className="scenario-header">
                                            <span className="scenario-index">{item.index}</span>
                                            <span className={`scenario-state ${item.state.toLowerCase()}`}>
                                                {getStateEmoji(item.state)} {item.state === 'UP' ? '상승' : item.state === 'DOWN' ? '하락' : '보합'}
                                            </span>
                                        </div>
                                        <p className="font-semibold mb-sm">{item.action}</p>
                                        {item.memo && <p className="text-sm text-muted">{item.memo}</p>}
                                    </div>
                                ))}
                                {ver.globalMemo && (
                                    <div className="mt-sm pt-sm border-t border-dashed">
                                        <p className="text-sm text-muted"><strong>메모:</strong> {ver.globalMemo}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {planVersions.length === 0 && !showForm && (
                    <p className="text-muted">아직 내일의 시나리오가 없습니다</p>
                )}
            </div>

            {/* Market Briefing */}
            <MarketBriefingSection date={date || todayKST} />
        </div>
    );
}


function MarketBriefingSection({ date }: { date: string }) {
    const [briefing, setBriefing] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [analysis, setAnalysis] = useState<any>(null);
    const [analyzing, setAnalyzing] = useState(false);

    useEffect(() => {
        loadBriefing();
        setAnalysis(null); // Reset analysis on date change
    }, [date]);

    const loadBriefing = async () => {
        setLoading(true);
        const res = await api.getMarketBriefing(date);
        if (res.success && res.data) {
            setBriefing(res.data);
        } else {
            setBriefing(null);
        }
        setLoading(false);
    };

    const handleFetch = async () => {
        setLoading(true);
        // Trigger fetch (POST) which updates DB then returns data
        const res = await api.request('/market-data/briefing/fetch', { method: 'POST' });
        if (res.success) {
            await loadBriefing();
        }
        setLoading(false);
    };

    const handleAnalyze = async () => {
        setAnalyzing(true);
        const res = await api.analyzeMarket(date);
        if (res.success) {
            setAnalysis(res.data);
        } else {
            alert('AI 분석에 실패했습니다: ' + (res.error || 'Unknown error'));
        }
        setAnalyzing(false);
    };

    if (loading && !briefing) return <div className="card mb-lg"><div className="loading-spinner"></div></div>;

    return (
        <div className="card mb-lg">
            <div className="card-header">
                <h3 className="card-title">📉 시장 브리핑 (리포트 & 뉴스)</h3>
                <button onClick={handleFetch} className="btn btn-sm btn-secondary" disabled={loading}>
                    {loading ? '업데이트 중...' : '🔄 최신 데이터 가져오기'}
                </button>
            </div>

            {!briefing ? (
                <div className="empty-state">
                    <p>오늘의 브리핑 데이터가 없습니다.</p>
                    <button onClick={handleFetch} className="btn btn-primary btn-sm mt-sm">데이터 가져오기</button>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-lg mb-lg">
                        {/* Reports */}
                        <div>
                            <h4 className="font-bold mb-md border-b pb-sm">📜 증권사 리포트</h4>
                            <div className="flex flex-col gap-sm">
                                {(briefing.reportSummary || []).length === 0 ? <p className="text-muted text-sm">리포트 없음</p> :
                                    (briefing.reportSummary || []).map((report: any, i: number) => (
                                        <a key={i} href={report.link} target="_blank" rel="noopener noreferrer" className="block p-sm border rounded hover:bg-gray-50 transition-colors text-decoration-none">
                                            <div className="font-semibold text-sm mb-xs text-primary">{report.title}</div>
                                            <div className="text-xs text-muted flex justify-between">
                                                <span>{report.broker}</span>
                                                <span>{report.date}</span>
                                            </div>
                                        </a>
                                    ))}
                            </div>
                        </div>

                        {/* News */}
                        <div>
                            <h4 className="font-bold mb-md border-b pb-sm">📰 주요 뉴스</h4>
                            <div className="flex flex-col gap-sm">
                                {(briefing.newsSummary || []).length === 0 ? <p className="text-muted text-sm">뉴스 없음</p> :
                                    (briefing.newsSummary || []).map((news: any, i: number) => (
                                        <a key={i} href={news.link} target="_blank" rel="noopener noreferrer" className="block p-sm border rounded hover:bg-gray-50 transition-colors text-decoration-none">
                                            <div className="font-semibold text-sm mb-xs">{news.title}</div>
                                            <p className="text-xs text-muted line-clamp-2 mb-xs">{news.summary}</p>
                                            <div className="text-xs text-muted text-right">{news.press}</div>
                                        </a>
                                    ))}
                            </div>
                        </div>
                    </div>

                    {/* AI Analysis Section */}
                    <div className="border-t pt-lg">
                        <div className="flex items-center justify-between mb-md">
                            <h4 className="font-bold flex items-center gap-sm" style={{ fontSize: '1.1rem' }}>
                                🤖 AI 시장 분석
                                <span className="badge badge-primary" style={{ fontSize: '0.7em' }}>GPT-4o</span>
                            </h4>
                            {!analysis && (
                                <button
                                    onClick={handleAnalyze}
                                    className="btn btn-primary btn-sm"
                                    disabled={analyzing}
                                >
                                    {analyzing ? '분석 중...' : '✨ AI 분석 실행'}
                                </button>
                            )}
                        </div>

                        {analyzing && (
                            <div className="text-center py-lg text-muted">
                                <div className="loading-spinner mb-sm mx-auto"></div>
                                <p>뉴스와 리포트를 분석하고 있습니다...</p>
                            </div>
                        )}

                        {analysis && (
                            <div className="bg-gray-50 p-md rounded" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                                <div className="mb-lg">
                                    <h5 className="font-semibold mb-sm text-primary">📊 시장 3줄 요약</h5>
                                    <p className="text-sm leading-relaxed">{analysis.summary}</p>
                                </div>

                                <div>
                                    <h5 className="font-semibold mb-sm text-primary">💡 추천 ETF</h5>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-md">
                                        {analysis.recommendations.map((rec: any, idx: number) => (
                                            <div key={idx} className="bg-white p-md rounded border shadow-sm" style={{ backgroundColor: 'var(--color-bg)' }}>
                                                <div className="flex justify-between items-start mb-xs">
                                                    <span className="font-bold text-lg text-primary">{rec.symbol}</span>
                                                </div>
                                                <div className="text-xs font-semibold text-muted mb-sm">{rec.name}</div>
                                                <p className="text-xs text-muted">{rec.reason}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
